'use client';

export default function PrivacyPolicyPage() {
    const primaryColor = '#33bdbf';
    const whiteColor = '#ffffff';
    const lightGray = '#f8f9fa';
    const darkGray = '#333333';

    const containerStyle = {
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '40px 20px',
        fontFamily: 'Montserrat, Roboto, sans-serif',
        backgroundColor: whiteColor,
        color: darkGray,
        lineHeight: '1.6',
    };

    const headerStyle = {
        backgroundColor: primaryColor,
        color: whiteColor,
        padding: '60px 20px',
        textAlign: 'center',
        marginBottom: '40px',
        borderRadius: '8px',
    };

    const headerTitleStyle = {
        fontSize: '42px',
        fontWeight: '700',
        margin: '0 0 10px 0',
    };

    const headerSubtitleStyle = {
        fontSize: '16px',
        fontWeight: '300',
        opacity: 0.9,
        margin: '0',
    };

    const sectionStyle = {
        marginBottom: '40px',
        paddingBottom: '30px',
        borderBottom: `1px solid #e0e0e0`,
    };

    const sectionTitleStyle = {
        fontSize: '24px',
        fontWeight: '600',
        color: primaryColor,
        marginBottom: '15px',
        marginTop: '20px',
    };

    const subsectionTitleStyle = {
        fontSize: '16px',
        fontWeight: '600',
        color: darkGray,
        marginTop: '15px',
        marginBottom: '10px',
    };

    const listItemStyle = {
        marginLeft: '20px',
        marginBottom: '8px',
        listStyleType: 'disc',
    };

    const lastSectionStyle = {
        ...sectionStyle,
        borderBottom: 'none',
    };

    const contactBoxStyle = {
        backgroundColor: lightGray,
        padding: '20px',
        borderRadius: '8px',
        borderLeft: `4px solid ${primaryColor}`,
        marginTop: '20px',
    };

    return (
        <div style={containerStyle}>
            {/* Header */}
            <div style={headerStyle}>
                <h1 style={headerTitleStyle}>Privacy Policy</h1>
                <p style={headerSubtitleStyle}>Last Updated: {new Date().toLocaleDateString()}</p>
            </div>

            {/* 1. Introduction */}
            <div style={sectionStyle}>
                <h2 style={sectionTitleStyle}>1. Introduction</h2>
                <p>
                    We respect your privacy and are committed to protecting your personal information. This Privacy Policy
                    explains how we collect, use, disclose, and safeguard your information when you use our application and
                    services, including all related platforms and websites.
                </p>
                <p>
                    Please read this Privacy Policy carefully. If you do not agree with our policies and practices, please do
                    not use our services.
                </p>
            </div>

            {/* 2. Information We Collect */}
            <div style={sectionStyle}>
                <h2 style={sectionTitleStyle}>2. Information We Collect</h2>

                <h3 style={subsectionTitleStyle}>2.1 Information You Provide Directly</h3>
                <p>We may collect information you provide when:</p>
                <ul style={{ marginLeft: '20px' }}>
                    <li style={listItemStyle}>Creating or updating your account</li>
                    <li style={listItemStyle}>Making purchases or transactions</li>
                    <li style={listItemStyle}>Contacting customer support</li>
                    <li style={listItemStyle}>Subscribing to newsletters or communications</li>
                    <li style={listItemStyle}>Responding to surveys or feedback requests</li>
                </ul>

                <h3 style={subsectionTitleStyle}>2.2 Information Collected Automatically</h3>
                <p>When you use our application, we may automatically collect:</p>
                <ul style={{ marginLeft: '20px' }}>
                    <li style={listItemStyle}>Device information (device type, operating system)</li>
                    <li style={listItemStyle}>Usage data (pages visited, features used, time spent)</li>
                    <li style={listItemStyle}>Log data (IP address, browser type, timestamps)</li>
                    <li style={listItemStyle}>Location information (with your consent)</li>
                    <li style={listItemStyle}>Cookies and similar tracking technologies</li>
                </ul>

                <h3 style={subsectionTitleStyle}>2.3 Third-Party Information</h3>
                <p>
                    We may receive information about you from third parties, including business partners, service providers,
                    and publicly available sources, which we may combine with other information we collect about you.
                </p>
            </div>

            {/* 3. How We Use Your Information */}
            <div style={sectionStyle}>
                <h2 style={sectionTitleStyle}>3. How We Use Your Information</h2>
                <p>We use the information we collect to:</p>
                <ul style={{ marginLeft: '20px' }}>
                    <li style={listItemStyle}>Provide, maintain, and improve our services</li>
                    <li style={listItemStyle}>Process transactions and send related information</li>
                    <li style={listItemStyle}>Send promotional communications (with your consent)</li>
                    <li style={listItemStyle}>Respond to inquiries and provide customer support</li>
                    <li style={listItemStyle}>Analyze usage patterns and improve user experience</li>
                    <li style={listItemStyle}>Detect and prevent fraudulent transactions</li>
                    <li style={listItemStyle}>Comply with legal obligations</li>
                    <li style={listItemStyle}>Personalize content and recommendations</li>
                </ul>
            </div>

            {/* 4. Data Security */}
            <div style={sectionStyle}>
                <h2 style={sectionTitleStyle}>4. Data Security</h2>
                <p>
                    We implement appropriate technical and organizational measures to protect your personal information against
                    unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the
                    Internet is 100% secure. While we strive to protect your personal information, we cannot guarantee its
                    absolute security.
                </p>
            </div>

            {/* 5. Data Sharing and Disclosure */}
            <div style={sectionStyle}>
                <h2 style={sectionTitleStyle}>5. Data Sharing and Disclosure</h2>

                <h3 style={subsectionTitleStyle}>5.1 Service Providers</h3>
                <p>
                    We may share your information with third-party service providers who perform services on our behalf,
                    including payment processors, analytics providers, and communication platforms.
                </p>

                <h3 style={subsectionTitleStyle}>5.2 Legal Requirements</h3>
                <p>
                    We may disclose your information if required to do so by law or if we believe in good faith that such action
                    is necessary to comply with legal obligations or protect our rights.
                </p>

                <h3 style={subsectionTitleStyle}>5.3 Business Transfers</h3>
                <p>
                    If we are involved in a merger, acquisition, or sale of assets, your information may be transferred as part
                    of that transaction.
                </p>

                <h3 style={subsectionTitleStyle}>5.4 With Your Consent</h3>
                <p>We may share your information with third parties when you provide explicit consent to do so.</p>
            </div>

            {/* 6. Your Rights and Choices */}
            <div style={sectionStyle}>
                <h2 style={sectionTitleStyle}>6. Your Rights and Choices</h2>

                <h3 style={subsectionTitleStyle}>6.1 Access and Correction</h3>
                <p>You have the right to access and correct your personal information held by us.</p>

                <h3 style={subsectionTitleStyle}>6.2 Opt-Out</h3>
                <p>
                    You can opt out of receiving promotional communications by following the unsubscribe instructions in our
                    emails or by contacting us directly.
                </p>

                <h3 style={subsectionTitleStyle}>6.3 Data Deletion</h3>
                <p>
                    You may request the deletion of your personal information, subject to certain legal or operational
                    requirements.
                </p>

                <h3 style={subsectionTitleStyle}>6.4 Cookie Management</h3>
                <p>You can control cookies through your browser settings, though some features may not work without them.</p>
            </div>

            {/* 7. Cookies and Tracking */}
            <div style={sectionStyle}>
                <h2 style={sectionTitleStyle}>7. Cookies and Tracking Technologies</h2>
                <p>
                    We use cookies and similar technologies to enhance your experience, remember your preferences, and analyze
                    usage patterns. Most web browsers allow you to control cookies through their settings. Be aware that disabling
                    cookies may affect the functionality of our services.
                </p>
            </div>

            {/* 8. Third-Party Links */}
            <div style={sectionStyle}>
                <h2 style={sectionTitleStyle}>8. Third-Party Links</h2>
                <p>
                    Our services may contain links to third-party websites. This Privacy Policy does not apply to external sites,
                    and we are not responsible for their privacy practices. Please review their privacy policies before providing
                    any personal information.
                </p>
            </div>

            {/* 9. Children's Privacy */}
            <div style={sectionStyle}>
                <h2 style={sectionTitleStyle}>9. Children's Privacy</h2>
                <p>
                    Our services are not intended for children under the age of 13. We do not knowingly collect personal
                    information from children. If we become aware that a child has provided us with personal information, we will
                    take steps to delete such information and terminate the child's account.
                </p>
            </div>

            {/* 10. Policy Updates */}
            <div style={sectionStyle}>
                <h2 style={sectionTitleStyle}>10. Updates to This Privacy Policy</h2>
                <p>
                    We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated
                    "Last Updated" date. Your continued use of our services following the posting of revised Privacy Policy means
                    that you accept and agree to the changes.
                </p>
            </div>

            {/* 11. Contact Us */}
            <div style={lastSectionStyle}>
                <h2 style={sectionTitleStyle}>11. Contact Us</h2>
                <p>
                    If you have questions, concerns, or requests regarding this Privacy Policy or our privacy practices, please
                    contact us:
                </p>
                <div style={contactBoxStyle}>
                    <p style={{ marginTop: 0 }}>
                        <strong>Email:</strong> privacy@fitfind.com
                    </p>
                    <p style={{ marginBottom: 0 }}>
                        <strong>Response Time:</strong> We will respond to your inquiry within 30 days.
                    </p>
                </div>
            </div>

            {/* Footer */}
            <div
                style={{
                    backgroundColor: primaryColor,
                    color: whiteColor,
                    padding: '20px',
                    textAlign: 'center',
                    marginTop: '60px',
                    borderRadius: '8px',
                    fontSize: '14px',
                }}
            >
                <p style={{ margin: 0 }}>© {new Date().getFullYear()} All rights reserved.</p>
            </div>
        </div>
    );
}
